import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import {
  fetchAdminManualCollections,
  fetchAdminPaymentIntents,
  fetchAdminProviderEvents,
  fetchAdminWebhookEvents,
} from '../services/api/admin';
import type {
  AdminManualCollection,
  AdminManualCollectionSource,
  AdminManualCollectionStatus,
  AdminPaymentIntent,
  AdminPaymentState,
  AdminPayoutState,
  AdminProviderEvent,
  AdminWebhookEvent,
  AdminWebhookProcessingState,
} from '../types/adminPaymentOps';
import { formatCurrency, formatDateTime } from '../utils/companyOwner';
import '../styles/register.css';
import '../styles/company-owner.css';
import '../styles/admin.css';

const PAYMENT_STATES: AdminPaymentState[] = [
  'REQUIRES_PAYMENT_METHOD',
  'PAYMENT_METHOD_ATTACHED',
  'AUTH_PENDING',
  'AUTH_REQUIRED_ACTION',
  'AUTHORIZED',
  'AUTH_PARTIALLY_REVERSED',
  'AUTH_REVERSED',
  'AUTH_FAILED',
  'CAPTURE_PENDING',
  'CAPTURED',
  'CAPTURE_FAILED',
  'CANCELLED',
  'DISPUTED',
  'CHARGEBACK',
  'SETTLED',
];

const PAYOUT_STATES: AdminPayoutState[] = [
  'NOT_READY',
  'READY_FOR_BATCH',
  'IN_BATCH',
  'SUBMITTED',
  'PROCESSING',
  'PAID',
  'SPLIT_SETTLED',
  'FAILED',
  'ON_HOLD',
  'REVERSED',
];

const WEBHOOK_PROCESSING_STATES: AdminWebhookProcessingState[] = [
  'RECEIVED',
  'VALIDATED',
  'PROCESSED',
  'FAILED',
  'IGNORED_DUPLICATE',
];

const MANUAL_COLLECTION_SOURCES: AdminManualCollectionSource[] = ['CASH', 'DIRECT_EFT'];

const MANUAL_COLLECTION_STATUSES: AdminManualCollectionStatus[] = [
  'PARTIALLY_COLLECTED',
  'COLLECTED',
  'VOIDED',
];

const STATE_CLASS_MAP: Record<string, string> = {
  AUTHORIZED: 'admin-pill-active',
  CAPTURED: 'admin-pill-success',
  SETTLED: 'admin-pill-success',
  PAID: 'admin-pill-success',
  SPLIT_SETTLED: 'admin-pill-success',
  PROCESSING: 'admin-pill-active',
  SUBMITTED: 'admin-pill-active',
  IN_BATCH: 'admin-pill-active',
  READY_FOR_BATCH: 'admin-pill-active',
  AUTH_PENDING: 'admin-pill-warning',
  CAPTURE_PENDING: 'admin-pill-warning',
  AUTH_REQUIRED_ACTION: 'admin-pill-warning',
  ON_HOLD: 'admin-pill-warning',
  DISPUTED: 'admin-pill-warning',
  AUTH_FAILED: 'admin-pill-danger',
  CAPTURE_FAILED: 'admin-pill-danger',
  CANCELLED: 'admin-pill-danger',
  CHARGEBACK: 'admin-pill-danger',
  FAILED: 'admin-pill-danger',
  TERMINAL_FAILURE: 'admin-pill-danger',
  MANUAL_REVIEW_REQUIRED: 'admin-pill-warning',
  MANUAL_COLLECTION_REQUIRED: 'admin-pill-warning',
  PARTIALLY_COLLECTED: 'admin-pill-warning',
  COLLECTED: 'admin-pill-success',
  VOIDED: 'admin-pill-danger',
};

function formatStateLabel(value: string | null | undefined): string {
  return value ? value.replaceAll('_', ' ') : 'Not set';
}

function formatOperationalState(
  kind: 'payment' | 'refund' | 'payout' | 'recovery',
  value: string | null | undefined,
): string {
  if (!value) return 'Not set';
  if (kind === 'refund' && value === 'NONE') return 'No refund';
  if (kind === 'recovery' && value === 'NONE') return 'No issue';
  if (kind === 'payout' && value === 'NOT_READY') return 'Not ready for driver payout';
  if (kind === 'payout' && value === 'SPLIT_SETTLED') return 'Split settled by Paystack';
  return formatStateLabel(value);
}

function formatMoney(value: number | null | undefined, currency: string | null | undefined): string {
  if (value === null || value === undefined) return 'Not set';
  return formatCurrency(value);
}

function shortId(value: string | null | undefined): string {
  if (!value) return 'Not set';
  return value.length > 12 ? `${value.slice(0, 8)}...${value.slice(-4)}` : value;
}

function customerDisplayName(intent: AdminPaymentIntent): string {
  return intent.customerFullName?.trim() || intent.customerEmail?.trim() || 'Customer details unavailable';
}

function customerContact(intent: AdminPaymentIntent): string | null {
  if (intent.customerFullName?.trim() && intent.customerEmail?.trim()) return intent.customerEmail;
  return intent.customerMobileE164?.trim() || null;
}

function driverDisplayName(intent: AdminPaymentIntent): string {
  return intent.driverName?.trim() || `Driver profile ${shortId(intent.driverId)}`;
}

function operationalState(
  label: string,
  kind: 'payment' | 'refund' | 'payout' | 'recovery',
  value: string | null | undefined,
): JSX.Element {
  return (
    <span className="admin-payment-state-item">
      <small>{label}</small>
      <span className={`admin-pill ${value ? STATE_CLASS_MAP[value] || '' : ''}`}>
        {formatOperationalState(kind, value)}
      </span>
    </span>
  );
}

function detailValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return 'Not set';
  return String(value);
}

function collectionSourceLabel(value: string | null | undefined): string {
  if (value === 'DIRECT_EFT') return 'Direct EFT';
  if (value === 'CASH') return 'Cash';
  return formatStateLabel(value);
}

function groupCollectionsByDriver(collections: AdminManualCollection[]) {
  const groups = new Map<string, {
    driverId: string;
    count: number;
    totalCollected: number;
    totalAgreed: number;
    totalOutstanding: number;
    currency: string | null;
  }>();

  collections
    .filter((collection) => collection.status !== 'VOIDED')
    .forEach((collection) => {
      const driverId = collection.driverId || 'Unassigned driver';
      const current = groups.get(driverId) ?? {
        driverId,
        count: 0,
        totalCollected: 0,
        totalAgreed: 0,
        totalOutstanding: 0,
        currency: collection.currency ?? null,
      };
      current.count += 1;
      current.totalCollected += collection.amount ?? 0;
      current.totalAgreed += collection.agreedAmountSnapshot ?? 0;
      current.totalOutstanding += collection.outstandingAfter ?? 0;
      current.currency = current.currency ?? collection.currency ?? null;
      groups.set(driverId, current);
    });

  return Array.from(groups.values()).sort((left, right) => right.totalCollected - left.totalCollected);
}

function renderJsonBlock(label: string, value: string | null | undefined): JSX.Element | null {
  if (!value) return null;

  let displayValue = value;
  try {
    displayValue = JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    displayValue = value;
  }

  return (
    <details className="admin-payment-json-detail">
      <summary>{label}</summary>
      <pre className="admin-json-block">{displayValue}</pre>
    </details>
  );
}

const AdminPaymentOpsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [paymentIntents, setPaymentIntents] = useState<AdminPaymentIntent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeLookup, setActiveLookup] = useState(searchParams.get('lookup') || 'payment-intents');
  const [providerEvents, setProviderEvents] = useState<AdminProviderEvent[]>([]);
  const [providerLoading, setProviderLoading] = useState(false);
  const [providerError, setProviderError] = useState('');
  const [providerLookup, setProviderLookup] = useState({
    paymentIntentPublicId: searchParams.get('paymentIntentPublicId') || '',
    provider: searchParams.get('provider') || '',
    providerReference: searchParams.get('providerReference') || '',
    limit: searchParams.get('limit') || '50',
  });
  const [webhookEvents, setWebhookEvents] = useState<AdminWebhookEvent[]>([]);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookError, setWebhookError] = useState('');
  const [webhookLookup, setWebhookLookup] = useState({
    provider: searchParams.get('provider') || '',
    providerEventId: searchParams.get('providerEventId') || '',
    eventType: searchParams.get('eventType') || '',
    processingState: searchParams.get('processingState') || '',
    limit: searchParams.get('limit') || '50',
  });
  const [manualCollections, setManualCollections] = useState<AdminManualCollection[]>([]);
  const [manualCollectionLoading, setManualCollectionLoading] = useState(false);
  const [manualCollectionError, setManualCollectionError] = useState('');
  const [manualCollectionLookup, setManualCollectionLookup] = useState({
    paymentIntentPublicId: searchParams.get('paymentIntentPublicId') || '',
    driverId: searchParams.get('driverId') || '',
    source: searchParams.get('source') || '',
    status: searchParams.get('status') || '',
    limit: searchParams.get('limit') || '50',
  });

  const filters = useMemo(
    () => ({
      publicId: searchParams.get('publicId') || '',
      tenantKey: searchParams.get('tenantKey') || '',
      bookingOwnerType: searchParams.get('bookingOwnerType') || '',
      bookingOwnerId: searchParams.get('bookingOwnerId') || '',
      tripGroupId: searchParams.get('tripGroupId') || '',
      primaryBookingId: searchParams.get('primaryBookingId') || '',
      userId: searchParams.get('userId') || '',
      driverId: searchParams.get('driverId') || '',
      provider: searchParams.get('provider') || '',
      providerIntentId: searchParams.get('providerIntentId') || '',
      paymentState: searchParams.get('paymentState') || '',
      payoutState: searchParams.get('payoutState') || '',
      limit: Number(searchParams.get('limit') || '50'),
    }),
    [searchParams],
  );

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const results = await fetchAdminPaymentIntents({
          publicId: filters.publicId.trim() || undefined,
          tenantKey: filters.tenantKey.trim() || undefined,
          bookingOwnerType: filters.bookingOwnerType.trim() || undefined,
          bookingOwnerId: filters.bookingOwnerId.trim() || undefined,
          tripGroupId: filters.tripGroupId.trim() || undefined,
          primaryBookingId: filters.primaryBookingId.trim() || undefined,
          userId: filters.userId.trim() || undefined,
          driverId: filters.driverId.trim() || undefined,
          provider: filters.provider.trim() || undefined,
          providerIntentId: filters.providerIntentId.trim() || undefined,
          paymentState: (filters.paymentState || undefined) as AdminPaymentState | undefined,
          payoutState: (filters.payoutState || undefined) as AdminPayoutState | undefined,
          limit: Number.isFinite(filters.limit) ? filters.limit : 50,
        });

        if (mounted) setPaymentIntents(results);
      } catch (loadError: any) {
        if (mounted) setError(loadError.message || 'Could not load payment intents.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, [filters]);

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setSearchParams(next);
  };

  const clearFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  useEffect(() => {
    if (activeLookup !== 'provider-events') return;

    setProviderLookup({
      paymentIntentPublicId: searchParams.get('paymentIntentPublicId') || '',
      provider: searchParams.get('provider') || '',
      providerReference: searchParams.get('providerReference') || '',
      limit: searchParams.get('limit') || '50',
    });
  }, [activeLookup, searchParams]);

  useEffect(() => {
    if (activeLookup !== 'webhook-events') return;

    setWebhookLookup({
      provider: searchParams.get('provider') || '',
      providerEventId: searchParams.get('providerEventId') || '',
      eventType: searchParams.get('eventType') || '',
      processingState: searchParams.get('processingState') || '',
      limit: searchParams.get('limit') || '50',
    });
  }, [activeLookup, searchParams]);

  useEffect(() => {
    if (activeLookup !== 'manual-collections') return;

    setManualCollectionLookup({
      paymentIntentPublicId: searchParams.get('paymentIntentPublicId') || '',
      driverId: searchParams.get('driverId') || '',
      source: searchParams.get('source') || '',
      status: searchParams.get('status') || '',
      limit: searchParams.get('limit') || '50',
    });
  }, [activeLookup, searchParams]);

  const selectLookup = (lookup: string) => {
    setActiveLookup(lookup);
    const next = new URLSearchParams(searchParams);
    if (lookup === 'payment-intents') {
      next.delete('lookup');
    } else {
      next.set('lookup', lookup);
    }
    setSearchParams(next);
  };

  const updateProviderLookup = (key: keyof typeof providerLookup, value: string) => {
    setProviderLookup((current) => ({ ...current, [key]: value }));
  };

  const updateWebhookLookup = (key: keyof typeof webhookLookup, value: string) => {
    setWebhookLookup((current) => ({ ...current, [key]: value }));
  };

  const updateManualCollectionLookup = (key: keyof typeof manualCollectionLookup, value: string) => {
    setManualCollectionLookup((current) => ({ ...current, [key]: value }));
  };

  const searchProviderEvents = async () => {
    const paymentIntentPublicId = providerLookup.paymentIntentPublicId.trim();
    const provider = providerLookup.provider.trim();
    const providerReference = providerLookup.providerReference.trim();

    if (!paymentIntentPublicId && (!provider || !providerReference)) {
      setProviderError('Search by payment intent public ID, or enter both provider and provider reference.');
      setProviderEvents([]);
      return;
    }

    setProviderLoading(true);
    setProviderError('');
    try {
      const results = await fetchAdminProviderEvents({
        paymentIntentPublicId: paymentIntentPublicId || undefined,
        provider: paymentIntentPublicId ? undefined : provider || undefined,
        providerReference: paymentIntentPublicId ? undefined : providerReference || undefined,
        limit: Number(providerLookup.limit) || 50,
      });
      setProviderEvents(results);
      const next = new URLSearchParams(searchParams);
      next.set('lookup', 'provider-events');
      if (paymentIntentPublicId) {
        next.set('paymentIntentPublicId', paymentIntentPublicId);
        next.delete('provider');
        next.delete('providerReference');
      } else {
        next.set('provider', provider);
        next.set('providerReference', providerReference);
        next.delete('paymentIntentPublicId');
      }
      next.set('limit', providerLookup.limit);
      setSearchParams(next);
    } catch (loadError: any) {
      setProviderError(loadError.message || 'Could not load provider events.');
      setProviderEvents([]);
    } finally {
      setProviderLoading(false);
    }
  };

  const searchWebhookEvents = async () => {
    setWebhookLoading(true);
    setWebhookError('');
    try {
      const results = await fetchAdminWebhookEvents({
        provider: webhookLookup.provider.trim() || undefined,
        providerEventId: webhookLookup.providerEventId.trim() || undefined,
        eventType: webhookLookup.eventType.trim() || undefined,
        processingState: (webhookLookup.processingState || undefined) as AdminWebhookProcessingState | undefined,
        limit: Number(webhookLookup.limit) || 50,
      });
      setWebhookEvents(results);
      const next = new URLSearchParams(searchParams);
      next.set('lookup', 'webhook-events');
      ['provider', 'providerEventId', 'eventType', 'processingState', 'limit'].forEach((key) => {
        const value = webhookLookup[key as keyof typeof webhookLookup].trim();
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
      });
      setSearchParams(next);
    } catch (loadError: any) {
      setWebhookError(loadError.message || 'Could not load webhook events.');
      setWebhookEvents([]);
    } finally {
      setWebhookLoading(false);
    }
  };

  const searchManualCollections = async () => {
    setManualCollectionLoading(true);
    setManualCollectionError('');
    try {
      const results = await fetchAdminManualCollections({
        paymentIntentPublicId: manualCollectionLookup.paymentIntentPublicId.trim() || undefined,
        driverId: manualCollectionLookup.driverId.trim() || undefined,
        source: manualCollectionLookup.source.trim() || undefined,
        status: manualCollectionLookup.status.trim() || undefined,
        limit: Number(manualCollectionLookup.limit) || 50,
      });
      setManualCollections(results);
      const next = new URLSearchParams(searchParams);
      next.set('lookup', 'manual-collections');
      ['paymentIntentPublicId', 'driverId', 'source', 'status', 'limit'].forEach((key) => {
        const value = manualCollectionLookup[key as keyof typeof manualCollectionLookup].trim();
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
      });
      setSearchParams(next);
    } catch (loadError: any) {
      setManualCollectionError(loadError.message || 'Could not load manual collections.');
      setManualCollections([]);
    } finally {
      setManualCollectionLoading(false);
    }
  };

  const manualCollectionDriverGroups = useMemo(
    () => groupCollectionsByDriver(manualCollections),
    [manualCollections],
  );

  useEffect(() => {
    if (activeLookup === 'provider-events') {
      const hasProviderCriteria =
        Boolean(searchParams.get('paymentIntentPublicId'))
        || (Boolean(searchParams.get('provider')) && Boolean(searchParams.get('providerReference')));
      if (hasProviderCriteria && !providerEvents.length && !providerLoading && !providerError) {
        void searchProviderEvents();
      }
    }
    if (activeLookup === 'webhook-events') {
      const hasWebhookCriteria = ['provider', 'providerEventId', 'eventType', 'processingState'].some((key) => Boolean(searchParams.get(key)));
      if (hasWebhookCriteria && !webhookEvents.length && !webhookLoading && !webhookError) {
        void searchWebhookEvents();
      }
    }
    if (activeLookup === 'manual-collections') {
      const hasManualCriteria = ['paymentIntentPublicId', 'driverId', 'source', 'status'].some((key) => Boolean(searchParams.get(key)));
      if (hasManualCriteria && !manualCollections.length && !manualCollectionLoading && !manualCollectionError) {
        void searchManualCollections();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLookup]);

  return (
    <AdminShell
      badge="Payment Operations"
      copy="Search canonical payment intents, inspect money states, and open the complete detail trail for operational review."
      title="Payment operations"
    >
      {error ? <div className="register-inline-error">{error}</div> : null}

      <div className="admin-payment-tabs" role="tablist" aria-label="Payment operations lookups">
        <button
          aria-selected={activeLookup === 'payment-intents'}
          className={`admin-payment-tab${activeLookup === 'payment-intents' ? ' admin-payment-tab-active' : ''}`}
          onClick={() => selectLookup('payment-intents')}
          role="tab"
          type="button"
        >
          Payment intents
        </button>
        <button
          aria-selected={activeLookup === 'provider-events'}
          className={`admin-payment-tab${activeLookup === 'provider-events' ? ' admin-payment-tab-active' : ''}`}
          onClick={() => selectLookup('provider-events')}
          role="tab"
          type="button"
        >
          Provider events
        </button>
        <button
          aria-selected={activeLookup === 'webhook-events'}
          className={`admin-payment-tab${activeLookup === 'webhook-events' ? ' admin-payment-tab-active' : ''}`}
          onClick={() => selectLookup('webhook-events')}
          role="tab"
          type="button"
        >
          Webhook events
        </button>
        <button
          aria-selected={activeLookup === 'manual-collections'}
          className={`admin-payment-tab${activeLookup === 'manual-collections' ? ' admin-payment-tab-active' : ''}`}
          onClick={() => selectLookup('manual-collections')}
          role="tab"
          type="button"
        >
          Manual collections
        </button>
      </div>

      {activeLookup === 'payment-intents' ? (
        <>
      <section className="admin-panel">
        <div className="admin-panel-header">
          <div>
            <h2 className="admin-panel-title">Payment intent search</h2>
            <p className="admin-panel-copy">
              Search by ShiftWagon IDs, booking owner fields, provider references, and money-state filters.
            </p>
          </div>
          <button className="register-secondary-button" onClick={clearFilters} type="button">
            Clear
          </button>
        </div>

        <div className="admin-toolbar admin-payment-ops-toolbar">
          <input
            className="register-input"
            onChange={(event) => updateFilter('publicId', event.target.value)}
            placeholder="Payment public ID"
            value={filters.publicId}
          />
          <input
            className="register-input"
            onChange={(event) => updateFilter('tenantKey', event.target.value)}
            placeholder="Tenant key"
            value={filters.tenantKey}
          />
          <select
            className="register-input"
            onChange={(event) => updateFilter('bookingOwnerType', event.target.value)}
            value={filters.bookingOwnerType}
          >
            <option value="">All owner types</option>
            <option value="TRIP_GROUP">Trip group</option>
            <option value="BOOKING">Booking</option>
          </select>
          <input
            className="register-input"
            onChange={(event) => updateFilter('bookingOwnerId', event.target.value)}
            placeholder="Booking owner ID"
            value={filters.bookingOwnerId}
          />
          <input
            className="register-input"
            onChange={(event) => updateFilter('tripGroupId', event.target.value)}
            placeholder="Trip group ID"
            value={filters.tripGroupId}
          />
          <input
            className="register-input"
            onChange={(event) => updateFilter('primaryBookingId', event.target.value)}
            placeholder="Primary booking ID"
            value={filters.primaryBookingId}
          />
          <input
            className="register-input"
            onChange={(event) => updateFilter('userId', event.target.value)}
            placeholder="Customer user ID"
            value={filters.userId}
          />
          <input
            className="register-input"
            onChange={(event) => updateFilter('driverId', event.target.value)}
            placeholder="Driver ID"
            value={filters.driverId}
          />
          <input
            className="register-input"
            onChange={(event) => updateFilter('provider', event.target.value)}
            placeholder="Provider"
            value={filters.provider}
          />
          <input
            className="register-input"
            onChange={(event) => updateFilter('providerIntentId', event.target.value)}
            placeholder="Provider intent ID"
            value={filters.providerIntentId}
          />
          <select
            className="register-input"
            onChange={(event) => updateFilter('paymentState', event.target.value)}
            value={filters.paymentState}
          >
            <option value="">All payment states</option>
            {PAYMENT_STATES.map((state) => (
              <option key={state} value={state}>
                {formatStateLabel(state)}
              </option>
            ))}
          </select>
          <select
            className="register-input"
            onChange={(event) => updateFilter('payoutState', event.target.value)}
            value={filters.payoutState}
          >
            <option value="">All payout states</option>
            {PAYOUT_STATES.map((state) => (
              <option key={state} value={state}>
                {formatStateLabel(state)}
              </option>
            ))}
          </select>
          <select
            className="register-input"
            onChange={(event) => updateFilter('limit', event.target.value)}
            value={String(filters.limit)}
          >
            <option value="25">25 results</option>
            <option value="50">50 results</option>
            <option value="100">100 results</option>
            <option value="200">200 results</option>
          </select>
        </div>
      </section>

      <section className="admin-panel" style={{ marginTop: '18px' }}>
        <div className="admin-panel-header">
          <div>
            <h2 className="admin-panel-title">Payment intents</h2>
            <p className="admin-panel-copy">
              {loading ? 'Loading payment intents.' : `${paymentIntents.length} payment intent records returned.`}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="register-loader" />
        ) : (
          <div className="admin-table">
            <div className="admin-table-head admin-table-head-payment-intents">
              <span>Booking</span>
              <span>Customer and driver</span>
              <span>Provider</span>
              <span>Amounts</span>
              <span>Operational status</span>
            </div>
            {paymentIntents.map((intent) => (
              <Link
                className="admin-table-row admin-table-row-button admin-table-row-payment-intents"
                key={intent.publicId}
                to={`/admin/payment-ops/${encodeURIComponent(intent.publicId)}`}
              >
                <span>
                  <strong>{intent.primaryBookingId || shortId(intent.publicId)}</strong>
                  <small>Primary booking ID</small>
                  <small>Payment intent {shortId(intent.publicId)}</small>
                </span>
                <span>
                  <strong>{customerDisplayName(intent)}</strong>
                  {customerContact(intent) ? <small>{customerContact(intent)}</small> : null}
                  <small>{driverDisplayName(intent)}</small>
                  <small>Customer account {shortId(intent.userId)}</small>
                </span>
                <span>
                  <strong>{intent.provider || 'Provider not set'}</strong>
                  <small>{intent.providerIntentId || 'Provider ref not set'}</small>
                  <small>Method {detailValue(intent.paymentMethodType)}</small>
                  <small>Method ID {shortId(intent.paymentMethodPublicId)}</small>
                  {intent.collectionSource ? <small>Collection: {formatStateLabel(intent.collectionSource)}</small> : null}
                  {intent.splitStatus ? <small>Split: {intent.splitStatus}</small> : null}
                  {intent.paystackSubaccountCode ? <small>Subaccount: {shortId(intent.paystackSubaccountCode)}</small> : null}
                  {intent.payoutTargetType || intent.payoutTargetId ? (
                    <small>Payout target: {detailValue(intent.payoutTargetType)} / {shortId(intent.payoutTargetId)}</small>
                  ) : null}
                </span>
                <span>
                  <strong>{formatMoney(intent.grossAmount ?? intent.capturedAmount ?? intent.quotedAmount, intent.currency)}</strong>
                  <small>Captured: {formatMoney(intent.capturedAmount, intent.currency)}</small>
                  {intent.agreedAmount !== null && intent.agreedAmount !== undefined ? (
                    <small>Agreed: {formatMoney(intent.agreedAmount, intent.currency)}</small>
                  ) : null}
                  {intent.manuallyCollectedAmount !== null && intent.manuallyCollectedAmount !== undefined ? (
                    <small>Offline collected: {formatMoney(intent.manuallyCollectedAmount, intent.currency)}</small>
                  ) : null}
                  {intent.outstandingAmount !== null && intent.outstandingAmount !== undefined ? (
                    <small>Outstanding: {formatMoney(intent.outstandingAmount, intent.currency)}</small>
                  ) : null}
                  <small>Refunded: {formatMoney(intent.refundedAmount, intent.currency)}</small>
                  <small>Driver net: {formatMoney(intent.driverNetAmount, intent.currency)}</small>
                </span>
                <span className="admin-payment-state-stack">
                  {operationalState('Payment', 'payment', intent.paymentState)}
                  {operationalState('Refund', 'refund', intent.refundState)}
                  {operationalState('Payout', 'payout', intent.payoutState)}
                  {operationalState('Recovery', 'recovery', intent.failureState)}
                </span>
              </Link>
            ))}
            {!paymentIntents.length ? (
              <p className="admin-empty">No payment intents matched the current filters.</p>
            ) : null}
          </div>
        )}
      </section>
        </>
      ) : null}

      {activeLookup === 'provider-events' ? (
        <section className="admin-panel">
          <div className="admin-panel-header">
            <div>
              <h2 className="admin-panel-title">Provider event lookup</h2>
              <p className="admin-panel-copy">
                Search by payment intent public ID, or by both provider and provider reference when support only has a Paystack reference.
              </p>
            </div>
            <button
              className="register-secondary-button"
              disabled={providerLoading}
              onClick={() => void searchProviderEvents()}
              type="button"
            >
              Search
            </button>
          </div>

          {providerError ? <div className="register-inline-error">{providerError}</div> : null}

          <div className="admin-toolbar admin-payment-ops-toolbar">
            <input
              className="register-input"
              onChange={(event) => updateProviderLookup('paymentIntentPublicId', event.target.value)}
              placeholder="Payment intent public ID"
              value={providerLookup.paymentIntentPublicId}
            />
            <input
              className="register-input"
              onChange={(event) => updateProviderLookup('provider', event.target.value)}
              placeholder="Provider e.g. paystack"
              value={providerLookup.provider}
            />
            <input
              className="register-input"
              onChange={(event) => updateProviderLookup('providerReference', event.target.value)}
              placeholder="Provider reference"
              value={providerLookup.providerReference}
            />
            <select
              className="register-input"
              onChange={(event) => updateProviderLookup('limit', event.target.value)}
              value={providerLookup.limit}
            >
              <option value="25">25 results</option>
              <option value="50">50 results</option>
              <option value="100">100 results</option>
              <option value="200">200 results</option>
            </select>
          </div>

          {providerLoading ? (
            <div className="register-loader" />
          ) : (
            <div className="admin-payment-timeline">
              {providerEvents.map((event) => (
                <article className="admin-payment-timeline-item" key={event.publicId}>
                  <div className="admin-payment-timeline-marker" />
                  <div className="admin-payment-timeline-body">
                    <div className="admin-payment-timeline-header">
                      <div>
                        <strong>{detailValue(event.eventType)}</strong>
                        <small>{formatDateTime(event.recordedAt)}</small>
                      </div>
                      <span className={`admin-pill ${event.success ? 'admin-pill-success' : 'admin-pill-danger'}`}>
                        {event.success ? 'Success' : 'Failed'}
                      </span>
                    </div>
                    <div className="admin-payment-meta-grid">
                      <small>Direction: {detailValue(event.eventDirection)}</small>
                      <small>Provider: {detailValue(event.provider)}</small>
                      <small>HTTP: {detailValue(event.httpStatus)}</small>
                      <small>Reference: {detailValue(event.providerReference)}</small>
                      <small>
                        Payment intent:{' '}
                        {event.paymentIntentPublicId ? (
                          <Link className="admin-row-link" to={`/admin/payment-ops/${encodeURIComponent(event.paymentIntentPublicId)}`}>
                            {shortId(event.paymentIntentPublicId)}
                          </Link>
                        ) : (
                          'Not set'
                        )}
                      </small>
                      <small>Event ID: {shortId(event.publicId)}</small>
                    </div>
                    {renderJsonBlock('Request JSON', event.requestJson)}
                    {renderJsonBlock('Response JSON', event.responseJson)}
                  </div>
                </article>
              ))}
              {!providerEvents.length ? (
                <p className="admin-empty admin-payment-empty">No provider events loaded. Enter lookup criteria and search.</p>
              ) : null}
            </div>
          )}
        </section>
      ) : null}

      {activeLookup === 'manual-collections' ? (
        <section className="admin-panel">
          <div className="admin-panel-header">
            <div>
              <h2 className="admin-panel-title">Manual collection lookup</h2>
              <p className="admin-panel-copy">
                Search admin-declared cash and direct EFT receipts, then group them by driver for payout review.
              </p>
            </div>
            <button
              className="register-secondary-button"
              disabled={manualCollectionLoading}
              onClick={() => void searchManualCollections()}
              type="button"
            >
              Search
            </button>
          </div>

          {manualCollectionError ? <div className="register-inline-error">{manualCollectionError}</div> : null}

          <div className="admin-toolbar admin-payment-ops-toolbar">
            <input
              className="register-input"
              onChange={(event) => updateManualCollectionLookup('paymentIntentPublicId', event.target.value)}
              placeholder="Payment intent public ID"
              value={manualCollectionLookup.paymentIntentPublicId}
            />
            <input
              className="register-input"
              onChange={(event) => updateManualCollectionLookup('driverId', event.target.value)}
              placeholder="Driver ID"
              value={manualCollectionLookup.driverId}
            />
            <select
              className="register-input"
              onChange={(event) => updateManualCollectionLookup('source', event.target.value)}
              value={manualCollectionLookup.source}
            >
              <option value="">All sources</option>
              {MANUAL_COLLECTION_SOURCES.map((source) => (
                <option key={source} value={source}>
                  {collectionSourceLabel(source)}
                </option>
              ))}
            </select>
            <select
              className="register-input"
              onChange={(event) => updateManualCollectionLookup('status', event.target.value)}
              value={manualCollectionLookup.status}
            >
              <option value="">All statuses</option>
              {MANUAL_COLLECTION_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {formatStateLabel(status)}
                </option>
              ))}
            </select>
            <select
              className="register-input"
              onChange={(event) => updateManualCollectionLookup('limit', event.target.value)}
              value={manualCollectionLookup.limit}
            >
              <option value="25">25 results</option>
              <option value="50">50 results</option>
              <option value="100">100 results</option>
              <option value="200">200 results</option>
            </select>
          </div>

          {manualCollectionLoading ? (
            <div className="register-loader" />
          ) : (
            <>
              {manualCollectionDriverGroups.length ? (
                <div className="admin-payment-detail-section">
                  <div className="admin-payment-section-heading">
                    <h3>Driver owed summary</h3>
                    <span>{manualCollectionDriverGroups.length} drivers</span>
                  </div>
                  <div className="admin-payment-record-list">
                    {manualCollectionDriverGroups.map((group) => (
                      <div className="admin-payment-record admin-payment-record-driver-owed" key={group.driverId}>
                        <div>
                          <span className="admin-payment-record-label">Driver</span>
                          <strong>{shortId(group.driverId)}</strong>
                          <small>{group.count} active manual collection records</small>
                        </div>
                        <div>
                          <span className="admin-payment-record-label">Collected</span>
                          <strong>{formatMoney(group.totalCollected, group.currency)}</strong>
                          <small>Total agreed snapshots: {formatMoney(group.totalAgreed, group.currency)}</small>
                        </div>
                        <div>
                          <span className="admin-payment-record-label">Outstanding</span>
                          <strong className={group.totalOutstanding > 0 ? 'admin-payment-danger-text' : ''}>
                            {formatMoney(group.totalOutstanding, group.currency)}
                          </strong>
                          <small>Payout review should use linked payment intent payout state.</small>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="admin-payment-detail-section">
                <div className="admin-payment-section-heading">
                  <h3>Manual collection records</h3>
                  <span>{manualCollections.length} records</span>
                </div>
                <div className="admin-payment-record-list">
                  {manualCollections.map((collection) => (
                    <div className="admin-payment-record admin-payment-record-manual" key={collection.publicId}>
                      <div>
                        <span className="admin-payment-record-label">Collection</span>
                        <strong>{shortId(collection.publicId)}</strong>
                        <small>Collected {formatDateTime(collection.collectedAt)}</small>
                        <small>Created {formatDateTime(collection.createdAt)}</small>
                      </div>
                      <div>
                        <span className="admin-payment-record-label">Source and status</span>
                        <strong>{collectionSourceLabel(collection.source)}</strong>
                        <span className={`admin-pill ${collection.status ? STATE_CLASS_MAP[collection.status] || '' : ''}`}>
                          {formatStateLabel(collection.status)}
                        </span>
                        <small>Loading: {collection.loadingAuthorized ? 'Authorized' : 'Not authorized'}</small>
                      </div>
                      <div>
                        <span className="admin-payment-record-label">Money</span>
                        <strong>{formatMoney(collection.amount, collection.currency)}</strong>
                        <small>Agreed: {formatMoney(collection.agreedAmountSnapshot, collection.currency)}</small>
                        <small>Outstanding after: {formatMoney(collection.outstandingAfter, collection.currency)}</small>
                      </div>
                      <div>
                        <span className="admin-payment-record-label">Links</span>
                        {collection.paymentIntentPublicId ? (
                          <Link className="admin-row-link" to={`/admin/payment-ops/${encodeURIComponent(collection.paymentIntentPublicId)}`}>
                            Payment {shortId(collection.paymentIntentPublicId)}
                          </Link>
                        ) : (
                          <strong>Payment intent not set</strong>
                        )}
                        <small>Driver {shortId(collection.driverId)}</small>
                        <small>Proof {detailValue(collection.proofReference)}</small>
                      </div>
                      <div>
                        <span className="admin-payment-record-label">Ops notes</span>
                        <strong>{detailValue(collection.receivedBy)}</strong>
                        <small>Declared by: {detailValue(collection.declaredBy)}</small>
                        <small>{detailValue(collection.reason)}</small>
                      </div>
                    </div>
                  ))}
                  {!manualCollections.length ? (
                    <p className="admin-empty admin-payment-empty">No manual collections loaded. Enter lookup criteria and search.</p>
                  ) : null}
                </div>
              </div>
            </>
          )}
        </section>
      ) : null}

      {activeLookup === 'webhook-events' ? (
        <section className="admin-panel">
          <div className="admin-panel-header">
            <div>
              <h2 className="admin-panel-title">Webhook event lookup</h2>
              <p className="admin-panel-copy">
                Search provider webhooks by provider, provider event ID, event type, or processing state.
              </p>
            </div>
            <button
              className="register-secondary-button"
              disabled={webhookLoading}
              onClick={() => void searchWebhookEvents()}
              type="button"
            >
              Search
            </button>
          </div>

          {webhookError ? <div className="register-inline-error">{webhookError}</div> : null}

          <div className="admin-toolbar admin-payment-ops-toolbar">
            <input
              className="register-input"
              onChange={(event) => updateWebhookLookup('provider', event.target.value)}
              placeholder="Provider e.g. paystack"
              value={webhookLookup.provider}
            />
            <input
              className="register-input"
              onChange={(event) => updateWebhookLookup('providerEventId', event.target.value)}
              placeholder="Provider event ID"
              value={webhookLookup.providerEventId}
            />
            <input
              className="register-input"
              onChange={(event) => updateWebhookLookup('eventType', event.target.value)}
              placeholder="Event type"
              value={webhookLookup.eventType}
            />
            <select
              className="register-input"
              onChange={(event) => updateWebhookLookup('processingState', event.target.value)}
              value={webhookLookup.processingState}
            >
              <option value="">All processing states</option>
              {WEBHOOK_PROCESSING_STATES.map((state) => (
                <option key={state} value={state}>
                  {formatStateLabel(state)}
                </option>
              ))}
            </select>
            <select
              className="register-input"
              onChange={(event) => updateWebhookLookup('limit', event.target.value)}
              value={webhookLookup.limit}
            >
              <option value="25">25 results</option>
              <option value="50">50 results</option>
              <option value="100">100 results</option>
              <option value="200">200 results</option>
            </select>
          </div>

          {webhookLoading ? (
            <div className="register-loader" />
          ) : (
            <div className="admin-payment-timeline">
              {webhookEvents.map((event) => (
                <article className="admin-payment-timeline-item" key={event.publicId}>
                  <div className="admin-payment-timeline-marker" />
                  <div className="admin-payment-timeline-body">
                    <div className="admin-payment-timeline-header">
                      <div>
                        <strong>{detailValue(event.eventType)}</strong>
                        <small>Received {formatDateTime(event.receivedAt)}</small>
                      </div>
                      <span className={`admin-pill ${STATE_CLASS_MAP[event.processingState || ''] || ''}`}>
                        {formatStateLabel(event.processingState)}
                      </span>
                    </div>
                    <div className="admin-payment-meta-grid">
                      <small>Provider: {detailValue(event.provider)}</small>
                      <small>Provider event: {detailValue(event.providerEventId)}</small>
                      <small>Signature: {event.signatureValid ? 'Valid' : 'Invalid'}</small>
                      <small>Processed: {formatDateTime(event.processedAt)}</small>
                      <small>Payload hash: {detailValue(event.payloadHash)}</small>
                      <small>Webhook ID: {shortId(event.publicId)}</small>
                    </div>
                    {event.failureMessage ? <p className="admin-payment-note">{event.failureMessage}</p> : null}
                    {renderJsonBlock('Payload JSON', event.payloadJson)}
                  </div>
                </article>
              ))}
              {!webhookEvents.length ? (
                <p className="admin-empty admin-payment-empty">No webhook events loaded. Enter lookup criteria and search.</p>
              ) : null}
            </div>
          )}
        </section>
      ) : null}
    </AdminShell>
  );
};

export default AdminPaymentOpsPage;
